import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Container, Divider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ImageGallery from "./ImageGallery";
import TitleAndDescription from "./TitleAndDescription";
import ProductFeaturesSection from "./ProductFeaturesSection";
import SizeSelector from "./SizeSelector";
import DietarySelector from "./DietarySelector";
import GiftPackagingSelector from "./GiftPackagingSelector";
import PriceDisplay from "./PriceDisplay";
import AddToCartButton from "./AddToCartButton";
import NoteSection from "./NoteSection";
import useGrazeBoxSelections from "../../../hooks/useGrazeBoxSelections";

const GrazeBoxDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;

  const defaultProduct = {
    sizes: [],
    dietaryOptions: [],
    images: [],
    name: "",
    shortDescription: "",
    reviewCount: 0,
    note: "",
  };

  const resolvedProduct = product || defaultProduct;

  const {
    selectedSize,
    setSelectedSize,
    selectedDiet,
    setSelectedDiet,
    giftPackaging,
    setGiftPackaging,
    totalPrice,
  } = useGrazeBoxSelections(resolvedProduct);

  useEffect(() => {
    if (!product) {
      navigate("/shop/graze-boxes");
    }
  }, [product, navigate]);

  if (!product) return null;

  return (
    <Container
      maxWidth="xl"
      sx={{ py: { xs: 4, md: 8 }, pb: isMobile ? 10 : 8 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 4, md: 12 },
        }}>
        {/* Left Side */}
        <Box
          sx={{
            flex: 1,
            position: { md: "sticky" },
            top: { md: 100 },
            alignSelf: { md: "flex-start" },
          }}>
          <ImageGallery images={resolvedProduct.images} />
          {!isMobile && (
            <>
              <NoteSection />
              <AddToCartButton
                product={resolvedProduct}
                selectedSize={selectedSize}
                selectedDiet={selectedDiet}
                giftPackaging={giftPackaging}
                totalPrice={totalPrice}
              />
            </>
          )}
        </Box>

        {/* Right Side */}
        <Box sx={{ flex: 2 }}>
          <TitleAndDescription
            name={resolvedProduct.name}
            shortDescription={resolvedProduct.shortDescription}
            reviewCount={resolvedProduct.reviewCount}
          />

          <PriceDisplay price={totalPrice} />

          <Divider sx={{ my: 3 }} />

          <ProductFeaturesSection />

          <Divider sx={{ my: 3 }} />

          <SizeSelector
            sizes={resolvedProduct.sizes}
            selectedSize={selectedSize}
            setSize={setSelectedSize}
          />

          <Divider sx={{ my: 3 }} />

          <DietarySelector
            dietaryOptions={resolvedProduct.dietaryOptions}
            selectedDiet={selectedDiet}
            selectedSize={selectedSize}
            setDiet={setSelectedDiet}
          />

          <Divider sx={{ my: 3 }} />

          <GiftPackagingSelector
            giftPackaging={giftPackaging}
            setGiftPackaging={setGiftPackaging}
          />

          {isMobile && (
            <NoteSection /> // moved here for mobile placement
          )}
        </Box>
      </Box>

      {/* Sticky Mobile Add to Cart Button */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            borderTop: "1px solid #eee",
            p: 2,
            zIndex: 1000,
            boxShadow: "0 -2px 6px rgba(0,0,0,0.05)",
          }}>
          <AddToCartButton
            product={resolvedProduct}
            selectedSize={selectedSize}
            selectedDiet={selectedDiet}
            giftPackaging={giftPackaging}
            totalPrice={totalPrice}
          />
        </Box>
      )}
    </Container>
  );
};

export default GrazeBoxDetailPage;
